import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const PushNotificationManager = () => {
    const [permission, setPermission] = useState<NotificationPermission>('default');
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if ('Notification' in window) {
            setPermission(Notification.permission);
        }
    }, []);

    const requestPermission = async () => {
        if (!('Notification' in window)) {
            toast({
                title: "Not Supported",
                description: "This browser does not support desktop notifications",
                variant: "destructive"
            });
            return;
        }

        setLoading(true);
        try {
            const result = await Notification.requestPermission();
            setPermission(result);

            if (result === 'granted') {
                // Here we would typically subscribe via PushManager and send subscription to backend
                // Since we don't have backend keys yet, we just confirm UI state

                // Example of what successful real subscription code would look like:
                /*
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: 'YOUR_PUBLIC_VAPID_KEY'
                });
                await sendToBackend(subscription);
                */

                // For demo/readiness purposes:
                navigator.serviceWorker.ready.then((registration) => {
                    registration.showNotification('Notifications Enabled', {
                        body: 'You will now receive updates from PawsIndia!',
                        icon: '/icons/icon-192x192.png'
                    });
                });

                toast({
                    title: "Notifications Enabled",
                    description: "You'll now receive updates!",
                });
            } else if (result === 'denied') {
                toast({
                    title: "Permission Denied",
                    description: "Please enable notifications in your browser settings to receive updates.",
                    variant: "destructive"
                });
            }
        } catch (error) {
            console.error("Error requesting notification permission:", error);
        } finally {
            setLoading(false);
        }
    };

    if (permission === 'granted' || permission === 'denied') {
        return null; // Don't show if decision already made (could move to settings page to allow change)
    }

    return (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:bottom-4 z-40 animate-in slide-in-from-bottom-5 pointer-events-none">
            <div className="bg-background/95 backdrop-blur border rounded-xl shadow-lg p-4 flex items-center justify-between gap-4 max-w-sm ml-auto pointer-events-auto">
                <div className="flex-1">
                    <h3 className="font-semibold text-sm">Enable Notifications</h3>
                    <p className="text-xs text-muted-foreground">Get updates on your pets and messages.</p>
                </div>
                <Button
                    size="sm"
                    onClick={requestPermission}
                    disabled={loading}
                    className="gap-2 rounded-xl shrink-0"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                    Enable
                </Button>
            </div>
        </div>
    );
};
